"use client";

import React from "react";
import Link from "next/link";

import { absoluteUrl, serializeJsonLd } from "@/lib/jsonld";
import useBreadcrumbs from "@/hooks/useBreadcrumbs";
import { cn } from "@/lib/utils";
import type { IBreadcrumbItem } from "@/types/breadcrumb";

type BreadcrumbVariant = "dark" | "light";

interface BreadcrumbProps {
  /**
   * Fully explicit trail, bypassing URL derivation. Only needed when the
   * hierarchy can't be read from the path — e.g. `/formation/:slug`, where the
   * formation's category sits between the catalog and the formation itself.
   */
  items?: IBreadcrumbItem[];
  /**
   * Label overrides for dynamic segments, keyed by segment or full path.
   * e.g. `{ "marketing-digital": "Marketing & Digital" }`.
   */
  labels?: Record<string, string>;
  /** Shortcut override for the last crumb, when only the page title is known. */
  currentLabel?: string;
  /** `dark` (default) sits on the navy heroes, `light` on white/grey sections. */
  variant?: BreadcrumbVariant;
  /**
   * Emit the BreadcrumbList JSON-LD. Turn off on `noindex` pages, and on a
   * second responsive copy of a trail already rendered on the same page.
   */
  withSchema?: boolean;
  className?: string;
}

const VARIANT_STYLES: Record<
  BreadcrumbVariant,
  { link: string; current: string; separator: string }
> = {
  dark: {
    link: "text-white/70 hover:text-white",
    current: "text-white font-bold",
    separator: "text-white/40",
  },
  light: {
    link: "text-[#676767] hover:text-[#1B365E]",
    current: "text-[#1B365E] font-bold",
    separator: "text-[#ABABAB]",
  },
};

const Separator: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="6"
    height="10"
    viewBox="0 0 6 10"
    fill="none"
    aria-hidden="true"
    className={cn("shrink-0", className)}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="m1 1 4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Builds the schema.org BreadcrumbList Google reads to render the trail in
 * search results. Absolute URLs are required, hence absoluteUrl().
 * The current page carries no `item` — it is where the user already is.
 */
function buildBreadcrumbSchema(items: IBreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.label,
      ...(item.href ? { item: absoluteUrl(item.href) } : {}),
    })),
  };
}

/**
 * Accessible breadcrumb trail, derived from the current route by default.
 *
 * Renders nothing when there is no meaningful hierarchy to show (home page,
 * full-screen routes), so it is safe to drop in unconditionally.
 */
const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  labels,
  currentLabel,
  variant = "dark",
  withSchema = true,
  className,
}) => {
  const derived = useBreadcrumbs({ labels, currentLabel });
  const trail = items ?? derived;

  // A single crumb is just the page itself — nothing to navigate back to.
  if (trail.length < 2) return null;

  const styles = VARIANT_STYLES[variant];

  return (
    <nav
      data-print-hide
      aria-label="Fil d'Ariane"
      className={cn("w-full mb-3 sm:mb-4", className)}
    >
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 font-fustat text-[12px] sm:text-[14px] leading-5 tracking-[0.02em]">
        {trail.map((item, index) => {
          const isLast = index === trail.length - 1;

          return (
            <li key={`${item.href ?? "current"}-${index}`} className="flex items-center gap-x-2">
              {item.href && !isLast ? (
                <Link
                  href={item.href}
                  title={item.label}
                  className={cn("transition-colors duration-200", styles.link)}
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  aria-current="page"
                  title={item.label}
                  className={cn(
                    "inline-block max-w-[55vw] sm:max-w-100 truncate align-bottom",
                    styles.current,
                  )}
                >
                  {item.label}
                </span>
              )}

              {!isLast && <Separator className={styles.separator} />}
            </li>
          );
        })}
      </ol>

      {withSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: serializeJsonLd(buildBreadcrumbSchema(trail)),
          }}
        />
      )}
    </nav>
  );
};

export default Breadcrumb;
